import { useEventListener } from '@vueuse/core'
import { toValue } from 'vue'

import type { PasteDetectionOptions } from '@/composables/use-paste-detection/usePasteDetection.types'

const isInputFocused = (): boolean => {
  const el = document.activeElement
  if (!el) return false
  if (el.hasAttribute('data-barcode-passthrough')) return false
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || el.hasAttribute('contenteditable')
}

const usePasteDetection = (options: PasteDetectionOptions) => {
  const { onPaste, delimiters = /[\n,]/, validate = (v: string) => v.length > 0, ignoreInputFocus = true } = options

  useEventListener(document, 'paste', (event: ClipboardEvent) => {
    if (toValue(options.enabled ?? true) === false) return
    if (ignoreInputFocus && isInputFocused()) return

    const text = event.clipboardData?.getData('text/plain')?.trim()
    if (!text) return

    const candidates = text
      .split(delimiters)
      .map(s => s.trim())
      .filter(validate)
    if (candidates.length > 0) {
      // Prevent native paste on passthrough inputs to avoid doubled text —
      // the barcode pipeline sets the value via injectBarcode → searchQuery → v-model,
      // so the native insertion would duplicate it.
      if (document.activeElement?.hasAttribute('data-barcode-passthrough')) {
        event.preventDefault()
      }
      onPaste(candidates)
    }
  })
}

export { usePasteDetection }
